import { Session } from './types/session';
import api from '.';
import { AxiosResponse } from 'axios';
import { PaginatedResponse } from './types/pagination';
import {
  CursorPaginatedResponse,
  SessionTimelineItem,
  Transcript
} from 'src/api/types/transcript';
import { SessionSummary } from './types/sessionSummary';
import { SessionStats } from './types/sessionStats';

export interface TimelineLegacyOptions {
  limit?: number;
  page?: number;
}

export interface TimelineCursorOptions {
  limit?: number;
  cursor?: string;
  direction?: 'older' | 'newer';
  /** Fetch a window centered on this timeline item id (deep links). */
  around?: string;
}

export type TimelineOptions = TimelineLegacyOptions | TimelineCursorOptions;

function isCursorOptions(opts: TimelineOptions): opts is TimelineCursorOptions {
  return 'cursor' in opts || 'direction' in opts;
}

const fetchSessions = async (limit = 10, page = 1) => {
  return await api.get<
    {
      limit: number;
      page: number;
    },
    AxiosResponse<PaginatedResponse<Session>>
  >('/sessions', {
    params: { limit, page }
  });
};

const fetchSessionsByCampaignId = async (
  campaignId: string,
  limit = 10,
  page = 1,
  sortBy?: string,
  debugSource?: string
) => {
  const params: { limit: number; page: number; sortBy?: string } = { limit, page };
  if (sortBy) {
    params.sortBy = sortBy;
  }

  return await api.get<
    {
      limit: number;
      page: number;
      sortBy?: string;
    },
    AxiosResponse<PaginatedResponse<Session>>
  >(`/campaigns/${campaignId}/sessions`, {
    params,
    headers: debugSource ? { 'X-Debug-Source': debugSource } : undefined
  });
};

const fetchSessionsById = async (sessionId: string) => {
  return await api.get<null, AxiosResponse<Session>>(`/sessions/${sessionId}`);
};

const fetchTranscriptsBySessionId = async (sessionId: string, limit = 10, page = 1) => {
  return await api.get<
    {
      limit: number;
      page: number;
    },
    AxiosResponse<PaginatedResponse<Transcript>>
  >(`/sessions/${sessionId}/transcripts`, {
    params: { limit, page }
  });
};

const fetchTimelineBySessionId = async (
  sessionId: string,
  limitOrOpts: number | TimelineOptions = 10,
  page = 1
): Promise<
  AxiosResponse<PaginatedResponse<SessionTimelineItem> | CursorPaginatedResponse<SessionTimelineItem>>
> => {
  if (typeof limitOrOpts === 'object' && isCursorOptions(limitOrOpts)) {
    const { limit = 50, cursor, direction = 'older' } = limitOrOpts;
    const params: Record<string, string | number> = { limit, direction };
    if (cursor) params.cursor = cursor;
    return api.get(`/sessions/${sessionId}/timeline`, { params });
  }

  const limit = typeof limitOrOpts === 'object' ? (limitOrOpts.limit ?? 10) : limitOrOpts;
  const pg = typeof limitOrOpts === 'object' ? (limitOrOpts.page ?? 1) : page;
  return api.get<
    { limit: number; page: number },
    AxiosResponse<PaginatedResponse<SessionTimelineItem>>
  >(`/sessions/${sessionId}/timeline`, { params: { limit, page: pg } });
};

const fetchTimelineCursor = async (
  sessionId: string,
  opts: TimelineCursorOptions = {}
): Promise<AxiosResponse<CursorPaginatedResponse<SessionTimelineItem>>> => {
  const { limit = 50, cursor, direction = 'older', around } = opts;
  const params: Record<string, string | number> = { limit, direction };
  if (cursor) params.cursor = cursor;
  if (around) params.around = around;
  return api.get(`/sessions/${sessionId}/timeline`, { params });
};

const updateSession = async (sessionId: string, sessionData: Partial<Session>) => {
  return await api.patch<Partial<Session>, AxiosResponse<Session>>(
    `/sessions/${sessionId}`,
    sessionData
  );
};

const deleteSession = async (sessionId: string) => {
  return await api.delete<null, AxiosResponse<void>>(`/sessions/${sessionId}`);
};

const fetchCurrentSession = async (debugSource?: string) => {
  return await api.get<null, AxiosResponse<Session | null>>('/sessions/current', {
    headers: debugSource ? { 'X-Debug-Source': debugSource } : undefined
  });
};

const createSession = async (campaignId: string, sessionData: Partial<Session> = {}) => {
  return await api.post<typeof sessionData & { campaignId: string }, AxiosResponse<Session>>(
    '/sessions',
    { ...sessionData, campaignId }
  );
};

const startSession = async (sessionId: string) => {
  return await api.patch<{ startDate: Date }, AxiosResponse<Session>>(`/sessions/${sessionId}`, {
    startDate: new Date()
  });
};

const fetchSessionStats = async (sessionId: string) => {
  return await api.get<null, AxiosResponse<SessionStats>>(`/sessions/${sessionId}/stats`);
};

const fetchSessionSummaries = async (sessionId: string) => {
  return await api.get<null, AxiosResponse<SessionSummary[]>>(`/sessions/${sessionId}/summaries`);
};

const fetchLatestSessionSummary = async (sessionId: string) => {
  return await api.get<null, AxiosResponse<SessionSummary>>(
    `/sessions/${sessionId}/summary/latest`
  );
};

const fetchLatestSessionRecap = async (sessionId: string) => {
  return await api.get<null, AxiosResponse<SessionSummary>>(`/sessions/${sessionId}/recap/latest`);
};

const updateSessionSummary = async (summaryId: string, summaryData: { summary: string }) => {
  return await api.patch<{ summary: string }, AxiosResponse<SessionSummary>>(
    `/sessions/summary/${summaryId}`,
    summaryData
  );
};

const regenerateSessionContent = async (
  sessionId: string,
  options: { regenerateSummary?: boolean; regenerateRecap?: boolean }
) => {
  return await api.post<
    { regenerateSummary?: boolean; regenerateRecap?: boolean },
    AxiosResponse<{
      message: string;
      queuedSummary: boolean;
      queuedRecap: boolean;
    }>
  >(`/sessions/${sessionId}/regenerate`, options);
};

const updateOneLiner = async (sessionId: string, oneLiner: string | null) => {
  return await api.patch<{ oneLiner: string | null }, AxiosResponse<SessionSummary>>(
    `/sessions/${sessionId}/one-liner`,
    { oneLiner }
  );
};

const fetchKeyObjectsBySummaryId = async (summaryId: string) => {
  return await api.get<null, AxiosResponse<{ data: unknown[]; count: number }>>(
    `/key-objects/summary/${summaryId}`
  );
};

const SessionsApi = {
  fetchSessions,
  fetchSessionsByCampaignId,
  fetchSessionsById,
  fetchTranscriptsBySessionId,
  fetchTimelineBySessionId,
  fetchTimelineCursor,
  fetchCurrentSession,
  updateSession,
  deleteSession,
  createSession,
  startSession,
  fetchSessionStats,
  fetchSessionSummaries,
  fetchLatestSessionSummary,
  fetchLatestSessionRecap,
  updateSessionSummary,
  updateOneLiner,
  regenerateSessionContent,
  fetchKeyObjectsBySummaryId
};

export default SessionsApi;
